import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { track } from '@eventbrite/datalayer-library';
import {
    DEFAULT_DESTINATION_EXPANSIONS,
    FormattedEvent,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { connect } from 'react-redux';
import { searchOrganicEvents } from '../../../../api/searchOrganicEvents';
import DiscoverVerticalEventCard from '../../../../components/DiscoverEventCard/DiscoverVerticalEventCard';
import { ConnectedProps, DiscoverState } from '../../../../types/index';
import { transformResponse } from '../../../categoryBrowse/hooks/queries/events';
import { HOMEPAGE_HALLOWEEN_HAPPENINGS_EVENTS } from '../../constants/analytics';
import {
    HALLOWEEN_HAPPENINGS_COLLECTION_IDS,
    HALLOWEEN_PRIORITY_EVENT_IDS,
} from '../../constants/category';
import { useIsTabletDevice } from '../../containers/HomepageBannerCarousel/hooks';
import styles from './HalloweenHappeningsEventsContainer.module.scss';

const HALLOWEEN_TITLE_1 = gettext('HALLOWEEN');
const HALLOWEEN_TITLE_2 = gettext('HAPPENINGS');
const HALLOWEEN_SUBTITLE_1 = gettext("WE'VE HANDPICKED");
const HALLOWEEN_SUBTITLE_2 = gettext('THESE EVENTS FOR YOU.');

type EventAccumulator = {
    priorityEvents: FormattedEvent[];
    nonPriorityEvents: FormattedEvent[];
};

const TitleDescription = React.memo(
    ({ currentPlace }: { currentPlace: string }) => {
        return (
            <div className={styles.titleSubtitleContainer}>
                <h2 className={styles.h2}>
                    {HALLOWEEN_TITLE_1}
                    <br />
                    {HALLOWEEN_TITLE_2}
                </h2>

                <h3 className={styles.h3}>
                    <span>{`${currentPlace.toUpperCase()}`}</span>{' '}
                    {HALLOWEEN_SUBTITLE_1}
                    <br />
                    {HALLOWEEN_SUBTITLE_2}
                </h3>
            </div>
        );
    },
);

export const HalloweenHappeningsEvents = ({
    isAuthenticated,
    isMobile,
    location,
    shareOptions,
    locale,
}: ReduxProps) => {
    React.useEffect(() => {
        if (location && location.currentPlace) {
            track({
                eventName: 'HomepageHalloweenHappeningsEventsView',
                eventData: {
                    searchLocation: location.currentPlace,
                },
            });
        }
    }, [location]);

    const currentPlaceId = location?.placeId ? location.placeId : '';
    const currentPlace = location?.currentPlace ? location.currentPlace : '';

    // Collection query for halloween happenings events
    const collection = useQuery(
        ['homepage', location],
        () =>
            searchOrganicEvents({
                event_search: {
                    places: [currentPlaceId],
                    dates: ['current_future'],
                    page_size: 50,
                    include_mature_events: true,
                    include_manual_events: true,
                    aggs: {},
                    collections_or: HALLOWEEN_HAPPENINGS_COLLECTION_IDS,
                    sort: 'date',
                    available: true,
                    image: true,
                },
                browse_surface: 'homepage',
                'expand.destination_event': DEFAULT_DESTINATION_EXPANSIONS,
            }),
        {
            select: transformResponse,
            refetchOnMount: false,
            enabled: true,
        },
    );

    const isTablet = useIsTabletDevice();
    if (collection.isLoading) {
        return (
            <div>
                <LoadingSkeleton
                    height={isMobile ? '614px' : isTablet ? '630px' : '384px'}
                />
            </div>
        );
    }

    if (!collection.data || collection.data?.events.length === 0) return null;

    const events: FormattedEvent[] =
        collection.data?.events.length !== 0 ? collection.data.events : [];

    const { priorityEvents, nonPriorityEvents } =
        events.reduce<EventAccumulator>(
            (acc, event) => {
                if (HALLOWEEN_PRIORITY_EVENT_IDS.includes(event.id)) {
                    acc.priorityEvents.push(event);
                } else {
                    acc.nonPriorityEvents.push(event);
                }
                return acc;
            },
            { priorityEvents: [], nonPriorityEvents: [] },
        );

    const sortedEvents = [...priorityEvents, ...nonPriorityEvents];

    const showNavigation = isTablet
        ? sortedEvents.length > 2
        : sortedEvents.length > 3;

    const {
        halloweenHappenings,
        halloweenContainer,
        simpleCarouselParent,
        halloweenEventCard,
    } = styles;

    return (
        <div className={halloweenHappenings}>
            <div className={halloweenContainer}>
                <TitleDescription currentPlace={currentPlace} />
                <div className={simpleCarouselParent}>
                    <SimpleCarousel
                        useSideNavigation={showNavigation}
                        slidesVisible={{ desktop: 3, tablet: 2 }}
                    >
                        {sortedEvents.map((event) => (
                            <div
                                className={halloweenEventCard}
                                data-heap-id="halloween-happenings-event-card-click"
                                key={event.id}
                            >
                                <DiscoverVerticalEventCard
                                    event={event}
                                    shareOptions={shareOptions}
                                    isAuthenticated={isAuthenticated}
                                    affCode="ehomefeatured"
                                    statsigLocationString={
                                        HOMEPAGE_HALLOWEEN_HAPPENINGS_EVENTS
                                    }
                                    locale={locale}
                                    isLazyImage
                                />
                            </div>
                        ))}
                    </SimpleCarousel>
                </div>
            </div>
        </div>
    );
};

const _mapStateToProps = ({
    app,
    user,
    location,
    shareOptions,
}: DiscoverState) => {
    return {
        isMobile: app.isMobile,
        isAuthenticated: user.isAuthenticated,
        location: location,
        shareOptions: shareOptions,
        locale: app.locale,
    };
};

type ReduxProps = ConnectedProps<typeof _mapStateToProps>;
export default connect(_mapStateToProps)(HalloweenHappeningsEvents);
